import { Heart } from "lucide-react";
import { useCommunityHearts } from "@/lib/CommunityHeartContext";
import { useLang } from "@/lib/i18n";

/**
 * Hearts show actual community endorsements: never visits, invented popularity,
 * a one-to-five rating or a star equivalent. Editorial picks are separate.
 */
export default function CommunityHeartButton({
  listingId, detail = false, overlay = false, compact = false, onAction = () => {},
}) {
  const { lang } = useLang();
  const es = lang === "es";
  const { countFor, hasHeart, toggleHeart, pendingFor, errorFor, ready } = useCommunityHearts();
  const count = countFor(listingId);
  const reacted = hasHeart(listingId);
  const pending = pendingFor(listingId);
  const error = errorFor(listingId);
  const countText = count.toLocaleString(es ? "es-PR" : "en-US");
  const accessible = !ready
    ? (es ? "Corazones no disponibles temporalmente" : "Community hearts temporarily unavailable")
    : reacted
      ? (es ? "Quitar mi corazón" : "Remove my heart")
      : (es ? "Dar un corazón" : "Give a heart");
  const title = !ready
    ? (es ? "Corazones no disponibles. Intenta más tarde." : "Hearts are unavailable. Try again later.")
    : es
      ? "Un corazón demuestra cariño; no confirma una visita ni es una puntuación."
      : "A heart shows appreciation, not a verified visit or a rating.";
  const label = count ? countText : (es ? "Sé el primero" : "Be first");

  return (
    <span className="inline-flex max-w-full flex-col items-start gap-0.5">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onAction?.();
          void toggleHeart(listingId);
        }}
        disabled={!ready || pending || !listingId}
        aria-pressed={reacted}
        aria-label={accessible + (count ? " · " + countText + (es ? (count === 1 ? " corazón" : " corazones") : (count === 1 ? " heart" : " hearts")) : "")}
        title={title}
        className={detail
          ? "inline-flex min-h-11 items-center gap-2 rounded-[5px] bg-[#171719] px-4 py-2 text-[12px] font-extrabold text-white transition hover:bg-[#a93036] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d13240] disabled:opacity-65 dark:bg-[#ef3340]"
          : overlay
            ? "inline-flex min-h-10 items-center gap-1.5 rounded-full border border-white/35 bg-[#171717]/80 px-3 text-[11px] font-black text-white backdrop-blur-md transition hover:bg-[#a93036] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-65"
            : compact
              ? "inline-flex min-h-9 items-center gap-1.5 rounded-[4px] border border-[#e3d6d4] bg-[#fff9f8] px-2.5 text-[10.5px] font-extrabold text-[#a92e3a] transition hover:border-[#d9414b] hover:bg-[#fff0ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b83740] disabled:opacity-60 dark:border-white/20 dark:bg-[#322324] dark:text-[#ffadb4]"
              : "inline-flex min-h-10 items-center gap-1.5 rounded-full border border-[#e3d6d4] bg-[#fff9f8] px-3 text-[11px] font-black text-[#a92e3a] transition hover:border-[#d9414b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b83740] disabled:opacity-60"
        }
      >
        <Heart
          aria-hidden="true"
          className={"h-[16px] w-[16px] shrink-0 " + (reacted ? "fill-current" : "")}
          strokeWidth={2.2}
        />
        <span className="whitespace-nowrap tabular-nums">{ready ? label : "···"}</span>
        {detail && <span className="font-semibold text-white/75">{es ? "corazones" : "community hearts"}</span>}
      </button>
      {error && <span role="status" className="max-w-[180px] text-[9px] font-semibold leading-tight text-[#a92e3a] dark:text-[#f4b5b5]">{es ? "No se guardó. Intenta otra vez." : "Couldn't save. Try again."}</span>}
    </span>
  );
}
